import { getMsgDeskRuntime } from '@/msgdeskRuntime/runtime/MsgDeskRuntime'
import { legacyOperationGate } from './legacyOperationGate'
import type {
  ProjectionSyncResult,
  SectionPagingSummary,
  SemanticMoveSyncMeta,
  SubsSection,
  SubUiState,
} from './types'

type ProjectionTreeStore = {
  mutate: {
    syncLegacyRuntime: (reason?: string) => boolean;
    setRenderMode: (mode: string) => void;
    setSubState: (elId: number, patch: Partial<SubUiState>) => void;
    showArray: (section: SubsSection, ids: number[]) => void;
    setCurrentPage: (section: SubsSection, page: number) => void;
    setSectionPaging: (section: SubsSection, summary: SectionPagingSummary) => void;
  };
};

let projectionTreeStore: ProjectionTreeStore | null = null;

// Checked ids stamped outside the root-page projection (foreign clones,
// auto-checked children of a checked parent). Tracked per section so the
// flag can be cleared on uncheck without a full subStates sweep.
const nestedCheckedBySection: Record<SubsSection, Set<number>> = {
  answers: new Set(),
  submsges: new Set(),
};

export function configureProjectionSync(store: ProjectionTreeStore): void {
  projectionTreeStore = store;
}

function getProjectionTreeStore(): ProjectionTreeStore {
  if (!projectionTreeStore) throw new Error('MsgDesk projection sync store is not configured');
  return projectionTreeStore;
}

function idsFromLegacyTreeItems(items: any): number[] {
  if (!Array.isArray(items)) return [];
  return items
    .map((item: any) => item?.[0]?.[0])
    .filter((id: unknown): id is number => typeof id === 'number');
}

function appendUniqueId(ids: number[], elId: number): void {
  if (elId > 0 && !ids.includes(elId)) ids.push(elId);
}

function idsFromRuntimeTreeKeys(subsTree: any): number[] {
  if (!subsTree || typeof subsTree !== 'object') return [];

  return Object.keys(subsTree)
    .filter(key => /^\d+$/.test(key))
    .map(key => Number(key))
    .sort((a, b) => b - a)
    .map(subNumber => {
      try {
        return Number(subsTree[subNumber]?.el_id?.() || 0);
      } catch {
        return 0;
      }
    })
    .filter((id, index, ids): id is number =>
      id > 0 && ids.indexOf(id) === index
    );
}

function idsFromRuntimeTreeObject(treeObj: any, isSubmsges: boolean, countMode: 'visible' | 'loaded' = 'visible'): number[] {
  try {
    const subsTree = treeObj?.subs?.(isSubmsges);
    if (!subsTree) return [];

    const ids: number[] = [];
    let subNumber = Number(treeObj?.start_id?.(isSubmsges) || 0);
    let count = countMode === 'loaded'
      ? Number(treeObj?.loaded?.(isSubmsges) || treeObj?.showed?.(false, isSubmsges) || 0)
      : Number(treeObj?.showed?.(false, isSubmsges) || treeObj?.loaded?.(isSubmsges) || 0);
    let guard = 0;

    while (subNumber > 0 && guard < 1000) {
      const treeNode = subsTree[subNumber];
      if (!treeNode || typeof treeNode.el_id !== 'function') break;

      const elId = Number(treeNode.el_id());
      appendUniqueId(ids, elId);

      count -= 1;
      if (count <= 0) break;

      subNumber = Number(treeNode.next_id?.() || 0);
      guard += 1;
    }

    if (countMode === 'loaded') {
      for (const elId of idsFromRuntimeTreeKeys(subsTree)) {
        appendUniqueId(ids, elId);
      }
      if (ids.length) return ids;
    }

    if (ids.length) return ids;
    const keyedIds = idsFromRuntimeTreeKeys(subsTree);
    return keyedIds.length ? keyedIds : idsFromLegacyTreeItems(subsTree);
  } catch {
    return [];
  }
}

function checkedIdsFromRuntimeTreeObject(treeObj: any, isSubmsges: boolean): number[] {
  try {
    const checkedData = treeObj?.checked_subs?.()?.[isSubmsges ? 1 : 0];
    const checkedMap = checkedData?.[2];
    if (!checkedMap || typeof checkedMap !== 'object') return [];

    return Object.keys(checkedMap)
      .filter(key => checkedMap[key] && /^\d+$/.test(key))
      .map(key => Number(key))
      .filter((id, index, ids): id is number =>
        id > 0 && ids.indexOf(id) === index
      );
  } catch {
    return [];
  }
}

export function normalizeProjectionMode(mode?: string): string {
  if (!mode) return 'msg';
  return mode;
}

export function projectionModeFromMenuSwitcher(switcherId: unknown): string | null {
  switch (Number(switcherId)) {
    case 0: return 'msg';
    case 5: return 'trash';
    case 6: return 'favs';
    case 8: return 'attachments';
    case 9: return 'answers';
    case 10: return 'submsges';
    case 13: return 'drafts';
    default: return null;
  }
}

function normalizeMoveProjectionMode(mode?: string): string {
  if (mode === 'subs') return 'msg';
  return normalizeProjectionMode(mode);
}

export function currentLegacyPagingMode(): string {
  const pagingObj = getMsgDeskRuntime().facade.pagingObj();
  try {
    const mode = pagingObj?.paging_mode?.();
    return typeof mode === 'string' && mode ? mode : 'msg';
  } catch {
    return 'msg';
  }
}

function currentLegacyPageObject(isSubmsges: boolean, mode = currentLegacyPagingMode()): any | null {
  const pagingObj = getMsgDeskRuntime().facade.pagingObj();
  if (!pagingObj || typeof pagingObj.paging_data !== 'function') return null;

  try {
    const pagingData = pagingObj.paging_data(mode);
    const sectionData = pagingData?.[isSubmsges ? 1 : 0];
    const meta = sectionData?.[0];
    const currentPage = Number(meta?.[4] || 1);
    return sectionData?.[currentPage] || null;
  } catch {
    return null;
  }
}

/**
 * Read the live paging facts the legacy renderer used to redraw its own bar:
 * pages meta ([2]=last page number, [3]=default count), the page's cumulative
 * counter, and the section tree count. The more rule is the exact
 * PagingController._set_more_info rule: tree count - counter.
 */
function sectionPagingFromPageObject(pageObj: any, page: number): SectionPagingSummary | null {
  try {
    const meta = pageObj?.pages_data?.(false);
    if (!meta) return null;
    const isSubmsges = !!meta[1];
    const treeObj = pageObj.subs_tree?.();
    const total = Number(treeObj?.count?.(isSubmsges) || 0);
    const shownThrough = Number(pageObj.counter?.() || 0);
    const pageSize = Number(meta[3] || 0);
    const lastPage = Number(meta[2] || 0);
    const moreCount = Math.max(0, total - shownThrough);
    const totalPages = lastPage > 0
      ? Math.max(page, lastPage)
      : Math.max(1, page + Math.ceil(moreCount / Math.max(1, pageSize)));
    return { currentPage: page, totalPages, total, shownThrough, moreCount, pageSize };
  } catch {
    return null;
  }
}

export function syncLegacyPageObject(pageObj: any, options: { updateRenderProjection?: boolean; mode?: string } = {}): ProjectionSyncResult | null {
  const treeStore = getProjectionTreeStore();
  if (!pageObj || typeof pageObj.show_range !== 'function') return null;
  const updateRenderProjection = options.updateRenderProjection !== false;

  const isSubmsges = !!pageObj.is_submsges?.();
  const section: SubsSection = isSubmsges ? 'submsges' : 'answers';
  const page = Number(pageObj.page_number?.() || 1);
  const range = pageObj.show_range?.();
  const currentRange = Array.isArray(range) ? range[0] : null;
  const startId = Number(currentRange?.[0] || 0);
  const stopId = Number(currentRange?.[2] || 0);
  let count = Number(currentRange?.[1] || 0);
  const treeObj = pageObj.subs_tree?.();
  const subsTree = treeObj?.subs?.(isSubmsges);

  if (!(startId > 0) || !subsTree) {
    if (updateRenderProjection) {
      treeStore.mutate.showArray(section, []);
      treeStore.mutate.setCurrentPage(section, page);
      const emptyPaging = sectionPagingFromPageObject(pageObj, page);
      if (emptyPaging) treeStore.mutate.setSectionPaging(section, emptyPaging);
    }
    return { section, page, ids: [], mode: options.mode };
  }

  const visibleIds: number[] = [];
  let subNumber = startId;
  let guard = 0;

  while (subNumber > 0 && guard < 1000) {
    const treeNode = subsTree[subNumber];
    if (!treeNode || typeof treeNode.el_id !== 'function') break;

    const elId = Number(treeNode.el_id());
    if (elId > 0) visibleIds.push(elId);

    if (stopId > 0 && subNumber <= stopId) break;
    count -= 1;
    if (!(stopId > 0) && count <= 0) break;

    subNumber = Number(treeNode.next_id?.() || 0);
    guard += 1;
  }

  const loadedIds = idsFromRuntimeTreeObject(treeObj, isSubmsges, 'loaded');
  const ids = loadedIds.length ? loadedIds : visibleIds;
  const visibleSet = new Set(visibleIds);
  const checkedSet = new Set(checkedIdsFromRuntimeTreeObject(treeObj, isSubmsges));

  const projectedIds = ids;

  projectedIds.forEach(elId => {
    treeStore.mutate.setSubState(elId, {
      isVisible: visibleSet.has(elId),
      isFlagBlue: checkedSet.has(elId),
    });
  });

  // The legacy checker also checks rows outside the root-page projection
  // (foreign clones via _check_foreigners, children via iterate_subs_tree).
  // Stamp only their checked flag — visibility belongs to their own nested
  // render, not to this page's visibleSet.
  const projectedSet = new Set(projectedIds);
  const nestedChecked = new Set<number>();
  checkedSet.forEach(elId => {
    if (projectedSet.has(elId)) return;
    nestedChecked.add(elId);
    treeStore.mutate.setSubState(elId, { isFlagBlue: true });
  });
  nestedCheckedBySection[section].forEach(elId => {
    if (!nestedChecked.has(elId) && !projectedSet.has(elId)) {
      treeStore.mutate.setSubState(elId, { isFlagBlue: false });
    }
  });
  nestedCheckedBySection[section] = nestedChecked;

  if (updateRenderProjection) {
    treeStore.mutate.showArray(section, projectedIds);
    treeStore.mutate.setCurrentPage(section, page);
    const paging = sectionPagingFromPageObject(pageObj, page);
    if (paging) treeStore.mutate.setSectionPaging(section, paging);
  }

  return { section, page, ids: projectedIds, mode: options.mode };
}

export function syncLegacyModeProjection(
  mode: string,
  reason = 'manual',
  options: { updateRenderProjection?: boolean; sections?: Array<0 | 1> } = {}
): ProjectionSyncResult[] {
  const treeStore = getProjectionTreeStore();
  const normalizedMode = normalizeProjectionMode(mode);
  const updateRenderProjection = options.updateRenderProjection !== false;
  const sections: Array<0 | 1> = options.sections?.length
    ? options.sections
    : normalizedMode === 'answers'
      ? [0]
      : normalizedMode === 'submsges'
        ? [1]
        : [0, 1];
  const results: ProjectionSyncResult[] = [];

  treeStore.mutate.syncLegacyRuntime(reason);
  if (updateRenderProjection) treeStore.mutate.setRenderMode(normalizedMode);

  sections.forEach(sectionId => {
    const isSubmsges = sectionId === 1;
    const pageResult = syncLegacyPageObject(currentLegacyPageObject(isSubmsges, normalizedMode), {
      updateRenderProjection,
      mode: normalizedMode,
    });
    if (pageResult) results.push(pageResult);
  });

  return results;
}

export function syncCurrentLegacyProjection(reason = 'manual'): ProjectionSyncResult[] {
  const mode = currentLegacyPagingMode();
  const results = syncLegacyModeProjection(mode, reason, { updateRenderProjection: true });

  getMsgDeskRuntime().setGlobal('__MSG_LAST_PROJECTION_SYNC__', {
    reason,
    mode: normalizeProjectionMode(mode),
    results,
    timestamp: Date.now(),
  });

  return results;
}

export function syncAfterSemanticMove(meta: SemanticMoveSyncMeta, reason = 'semantic-move'): ProjectionSyncResult[] {
  const currentMode = normalizeProjectionMode(currentLegacyPagingMode());
  const sourceMode = normalizeProjectionMode(meta.sourceMode || currentMode);
  const destinationMode = normalizeMoveProjectionMode(meta.toMode);
  const modes = [currentMode, sourceMode, destinationMode].filter((mode, index, arr) =>
    !!mode && arr.indexOf(mode) === index
  );
  const results: ProjectionSyncResult[] = [];

  modes.forEach(mode => {
    const modeResults = syncLegacyModeProjection(mode, reason, {
      updateRenderProjection: mode === currentMode,
    });
    results.push(...modeResults);
  });

  const syncInfo = {
    reason,
    currentMode,
    sourceMode,
    destinationMode,
    section: meta.section,
    results,
    timestamp: Date.now(),
  };

  const runtime = getMsgDeskRuntime();
  runtime.setGlobal('__MSG_LAST_MOVE_PROJECTION_SYNC__', syncInfo);
  runtime.setGlobal('__MSG_LAST_PROJECTION_SYNC__', {
    reason,
    mode: currentMode,
    results: results.filter(result => result.mode === currentMode),
    timestamp: syncInfo.timestamp,
  });

  return results;
}

function shouldSyncAfterLegacyEvent(eventName: string): boolean {
  return [
    'mev_msg_get_more_subs_evm',
    'mev_msg_subs_set_page_evm',
    'mev_msg_toggle_subs_evm',
    'mev_msg_toggle_menu_subs_evm',
    'mev_msg_chain_load_more_subs_evm',
    'mev_msg_refresh_subs_evm',
    'mev_msg_menu_switcher_evm',
    'mev_msg_move_subs_evm',
    'mev_msg_check_sub_evm',
    'mev_msg_check_subs_evm',
    'mev_msg_toggle_fav_evm',
  ].includes(eventName);
}

function syncCurrentProjectionAfterLegacyEvent(reason: string): void {
  if (!getMsgDeskRuntime().browserWindow) return;
  try {
    syncCurrentLegacyProjection(reason);
  } catch {
    // Exact legacy completion hooks will run for async page/import/move paths.
  } finally {
    legacyOperationGate.noteProjectionSync(reason);
  }
}

export function syncProjectionAfterLegacyEvent(eventName: string): void {
  if (!shouldSyncAfterLegacyEvent(eventName)) return;
  syncCurrentProjectionAfterLegacyEvent(`legacy-event:${eventName}`);
}
